import {
  BodyText,
  Button,
  HeaderText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { useGetMultiplePatients } from "@/features/Patient/network/usePatient";
import { useActionHistory } from "@/hooks/useActionHistory";
import { useOpenPatientChart } from "@/hooks/useOpenPatientChart";
import { trackEvent } from "@/lib/tracking";
import { type ActionHistoryEntry, UserAction } from "@/types/actionHistory";
import type { Patient as PatientResource } from "@curai/js-fhir-tools";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";

const ACTION_LABELS: Record<UserAction, string> = {
  [UserAction.ENCOUNTER_CLOSED]: "Encounter Closed",
  [UserAction.ENCOUNTER_HANDED_OFF]: "Encounter Handed Off",
  [UserAction.ENCOUNTER_PAUSED]: "Visit Pended",
  [UserAction.PATIENT_TAB_CLOSED]: "Patient Tab Closed",
};

export const ActionHistoryTablePage = () => {
  const { actionHistory, clearActionHistory } = useActionHistory();
  const openPatientChart = useOpenPatientChart();

  useEffect(() => {
    trackEvent(TRACKERS.VIEW_ACTION_HISTORY_PAGE);
  }, []);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Calculate pagination values
  const totalItems = actionHistory.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageData = actionHistory.slice(startIndex, endIndex);

  // Extract unique patient IDs from current page data only
  const uniquePatientIds = useMemo(() => {
    const patientIds = currentPageData
      .map((entry) => entry.context.patientId)
      .filter((id): id is string => Boolean(id));
    return [...new Set(patientIds)];
  }, [currentPageData]);

  // Fetch all patients at once
  const patientQueries = useGetMultiplePatients(uniquePatientIds);

  // Create a map of patientId -> Patient for easy lookup
  const patientsMap: Map<string, PatientResource> = useMemo(() => {
    const map = new Map();
    patientQueries.forEach((query, index) => {
      if (query.data) {
        map.set(uniquePatientIds[index], query.data);
      }
    });
    return map;
  }, [patientQueries, uniquePatientIds]);

  const renderActionHistoryContext = (
    entry: ActionHistoryEntry,
  ): React.ReactNode[] => {
    const parts: React.ReactNode[] = [];

    if (entry.context.patientId) {
      const patient = patientsMap.get(entry.context.patientId);
      if (patient) {
        const patientName = patient.fullName;
        parts.push(
          <BodyText
            size="sm"
            textColor="secondary"
          >{`${patientName || entry.context.patientId}`}</BodyText>,
        );
      } else {
        parts.push(
          <BodyText
            size="sm"
            textColor="secondary"
          >{`${entry.context.patientId}`}</BodyText>,
        );
      }
    }

    if (entry.context.encounterId) {
      parts.push(
        <>
          <br />
          <BodyText
            size="xs"
            textColor="tertiary"
          >{`Visit: ${entry.context.encounterId}`}</BodyText>
        </>,
      );
    }

    return parts.length > 0
      ? parts
      : [
          <BodyText
            size="sm"
            textColor="secondary"
            key="action-history-no-context"
          >
            No additional context
          </BodyText>,
        ];
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleClearHistory = () => {
    clearActionHistory();
    setCurrentPage(1); // Reset to first page after clearing
  };

  return (
    <div className="p-6 max-w-7xl mx-auto overflow-auto">
      <div className="flex justify-between items-center mb-6">
        <HeaderText size="xl">Action History</HeaderText>
        <Button
          onClick={handleClearHistory}
          variant="danger"
          size="sm"
          disabled={actionHistory.length === 0}
        >
          Clear History
        </Button>
      </div>

      <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <HeaderText size="sm" className="text-blue-900 mb-2">
          EMR Action History
        </HeaderText>
        <BodyText size="sm" className="text-blue-800 mt-2">
          Actions are stored locally in your browser and limited to the most
          recent 100 entries. Entries are cleared on logout.
        </BodyText>
        <br />
        <BodyText size="sm" className="text-blue-800 mt-2">
          Currently supporting the following actions:
        </BodyText>
        <ul className="list-disc text-xs bold list-inside text-blue-800 mt-2 space-y-1">
          <li>Encounter Closed</li>
          <li>Encounter Handed Off</li>
          <li>Visit Pended</li>
          <li>Patient Tab Closed</li>
        </ul>
      </div>

      {actionHistory.length === 0 ? (
        <div className="text-center py-12">
          <BodyText size="lg" textColor="tertiary">
            No actions recorded yet.
          </BodyText>
          <br />
          <BodyText size="sm" textColor="tertiary" className="mt-2">
            Actions will appear here as you use the application.
          </BodyText>
        </div>
      ) : (
        <>
          <Table className="min-w-full">
            <TableHeader>
              <TableRow>
                <TableHead className="w-48">
                  <BodyText bold={true} size="sm" textColor="tertiary">
                    Action
                  </BodyText>
                </TableHead>
                <TableHead className="w-64">
                  <BodyText bold={true} size="sm" textColor="tertiary">
                    Context
                  </BodyText>
                </TableHead>
                <TableHead className="w-40">
                  <BodyText bold={true} size="sm" textColor="tertiary">
                    Date
                  </BodyText>
                </TableHead>
                <TableHead className="w-32">
                  <BodyText bold={true} size="sm" textColor="tertiary">
                    Time
                  </BodyText>
                </TableHead>
                <TableHead className="w-32">
                  <BodyText bold={true} size="sm" textColor="tertiary">
                    Navigate
                  </BodyText>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentPageData.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>
                    <BodyText size="sm" bold={true}>
                      {ACTION_LABELS[entry.action]}
                    </BodyText>
                  </TableCell>
                  <TableCell>
                    <BodyText size="sm" textColor="secondary">
                      {renderActionHistoryContext(entry)}
                    </BodyText>
                  </TableCell>
                  <TableCell>
                    <BodyText size="sm">
                      {format(new Date(entry.timestamp), "MMM d, yyyy")}
                    </BodyText>
                  </TableCell>
                  <TableCell>
                    <BodyText size="sm">
                      {format(new Date(entry.timestamp), "h:mm:ss a")}
                    </BodyText>
                  </TableCell>
                  <TableCell>
                    {entry.context.patientId ? (
                      <Button
                        variant="primary"
                        size="sm"
                        className="leading-tight"
                        onClick={async () =>
                          await openPatientChart({
                            patientId: entry.context.patientId,
                            encounterId: entry.context.encounterId,
                            medRecordDestination: "timeline",
                          })
                        }
                      >
                        Open Chart
                      </Button>
                    ) : (
                      <BodyText size="sm" textColor="tertiary">
                        N/A
                      </BodyText>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-6">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </Button>

              {/* Page numbers */}
              <div className="flex gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => {
                    // Show first page, last page, current page, and pages around current page
                    const showPage =
                      page === 1 ||
                      page === totalPages ||
                      Math.abs(page - currentPage) <= 1;

                    if (!showPage) {
                      // Show ellipsis for gaps
                      if (page === 2 && currentPage > 4) {
                        return (
                          <BodyText key={page} size="sm" className="px-2">
                            ...
                          </BodyText>
                        );
                      }
                      if (
                        page === totalPages - 1 &&
                        currentPage < totalPages - 3
                      ) {
                        return (
                          <BodyText key={page} size="sm" className="px-2">
                            ...
                          </BodyText>
                        );
                      }
                      return null;
                    }

                    return (
                      <Button
                        key={page}
                        variant={page === currentPage ? "primary" : "secondary"}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        className="min-w-[32px]"
                      >
                        {page}
                      </Button>
                    );
                  },
                )}
              </div>

              <Button
                variant="secondary"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          )}

          {/* Pagination info */}
          <div className="flex gap-1 mt-4 px-4 justify-between items-center mb-4">
            <BodyText size="sm" textColor="secondary">
              Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of{" "}
              {totalItems} entries
            </BodyText>
            <BodyText size="sm" textColor="secondary">
              Page {currentPage} of {totalPages}
            </BodyText>
          </div>
        </>
      )}
    </div>
  );
};
